import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AddBoltImage from '@/assets/visuals/add-bolt.webp';
import { PiFilePdf, PiArrowSquareOut } from "react-icons/pi";

import Dropzone from "@/components/common/Dropzone/Dropzone";
import FileScanner from "@/components/common/Dropzone/FileScanner";
import MotionText from "@/components/common/Motion/MotionText";
import MotionDiv from "@/components/common/Motion/MotionDiv";
import HeaderBack from "@/components/dashboard/Header/HeaderBack";

import ProgressBar from "@/components/common/ProgressBar/ProgressBar";

import { useKycStore } from "@/store/useKycStore";
import { useTourStore } from "@/store/useTourStore";

import { ExpenseCategory } from "@/types/expense";
import { useExpensesFormStore } from "@/store/useExpensesFormStore";

import Button from "@/components/common/Button/Button";

import { ocrExpense } from "@/services/ocr";
import { usePageSetup } from "@/hooks/usePageSetup";

export const BoltCommissionPage = () => {
  const { t } = useTranslation()
  usePageSetup(t('bolt.pageTitle.commission'))
  /* Reusing KYC */
  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(1), [])

  const navigate = useNavigate();

  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setExpensesForm = useExpensesFormStore((state) => state.setForm);

  const expensesDisclaimerAcknowledged = useTourStore(
    (state) => state.expensesDisclaimerAcknowledged
  );

  const handleFileSelect = async (file: File) => {
    setIsScanning(true);
    setError(null);

    const analysis = await ocrExpense(file, () => {
      setError(t('bolt.errors.commissionRecognitionFailed'));
      setIsScanning(false)
    })

    if (analysis) {
      const lineItems = analysis.line_items.filter((item) => item.amount > 0)
      const lineItem = lineItems[0]

      if (lineItem?.category !== "commission_fees") {
        setIsScanning(false);
        setError(t('bolt.errors.commissionNotFound'));
        return;
      }

      /* When bolt commission is uploaded
         There may be airport fees as well
         Sum total commission fees
      */
      const total = lineItems
        .filter((item) => item.category === "commission_fees")
        .reduce((total, item) => total + item.amount, 0)

      lineItem.amount = total

      const reference = analysis?.reference ? [
        analysis?.reference.date,
        analysis?.reference.type,
        analysis?.reference.number
      ].join(', ') : ''

      await setExpensesForm({
        file: file,
        category: lineItem?.category,
        operation: lineItem?.description,
        total: lineItem?.amount,
        date: analysis?.reference?.date,
        reference: reference
      });

      if (!expensesDisclaimerAcknowledged) {
        navigate('/expenses/disclaimer');
      } else {
        navigate('/expenses/create');
      }
    }
  };

  return (
    <div className="h-full bg-white flex flex-col">
      <HeaderBack to="/">
        <div className="flex flex-row items-center w-[8.5rem] ml-[3.84rem]">
          <ProgressBar
            currentStep={progress}
            totalSteps={2}
          />
        </div>
      </HeaderBack>
      <div className="h-full flex flex-col px-5 pb-[10rem]">
        <MotionText
          as="h2"
          className="font-semibold text-3xl text-zinc-900 mb-3"
          delay={0.1}
        >
          {t('bolt.commission.title')}
        </MotionText>
        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-2"
          delay={0.2}
        >
          {t('bolt.commission.description')}
        </MotionText>
        <MotionText
          as="p"
          className="text-zinc-700 text-xs mb-8"
          delay={0.2}
        >
          <Link className="underline" to="/bolt/commission/how">{t('bolt.commission.howToFind')}</Link>
        </MotionText>
        <MotionDiv
          className="bg-gray-100 p-4 rounded-lg flex flex-row gap-2 mb-4"
          delay={0.2}
        >
          <PiFilePdf className="h-6 w-6 text-black shrink-0" />
          <Link className="text-sm text-zinc-700 font-semibold flex-1 flex items-center gap-1" target="_blank" to="https://partners.bolt.eu/invoices">
            {t('bolt.commission.downloadPdf')} <span className="underline text-blue-600 hover:text-blue-700 inline-flex items-center gap-0.5">Bolt <PiArrowSquareOut className="w-4 h-4" /></span>
          </Link>
        </MotionDiv>
        {error && (
          <MotionText
            as="p"
            className="text-red-500 text-sm"
            delay={0.2}
          >
            {error}
          </MotionText>
        )}
        <MotionDiv
          className="flex flex-col gap-2"
          delay={0.3}
        >
          {isScanning ? (
            <FileScanner isScanning={isScanning} />
          ) : (
            <Dropzone
              onFileSelect={handleFileSelect}
              maxSize={10 * 1024 * 1024}
              buttonText={t('common.selectFile')}
            >
              <img src={AddBoltImage}
                className='w-[4.375rem] h-[5.75rem] mb-2'
                alt="Add Bolt"
              />
            </Dropzone>
          )}
        </MotionDiv>
      </div>
      <motion.div className="px-5 pb-8 flex flex-col gap-2 absolute bottom-0 w-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.2,
          ease: "easeOut"
        }}
        whileTap={{ scale: 0.98 }}
      >
        <Button
          variant='secondary'
          onClick={async () => {
            await setExpensesForm({
              category: 'commission_fees' as ExpenseCategory
            })

            if (!expensesDisclaimerAcknowledged) {
              navigate('/expenses/disclaimer');
            } else {
              navigate('/expenses/create');
            }
          }}
        >
          <span>{t('common.enterWithoutDocument')}</span>
        </Button>
      </motion.div>
    </div>
  );
};

export default BoltCommissionPage;